import { Frequency } from '@generated/graphql';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { HoldingSelectField } from '@ovComponents/3-pattern/holdingSelectField/holdingSelect';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { FISCAL_YEAR_SELECT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import { showFiscalYearBasedOnAccountType } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositFiscalYear';
import {
  type DepositScheduleOptions,
  DepositStarting,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositSchedule';
import type { useDepositSchedule } from '@ovComponents/4-module/workflowCompletion/subSteps/utils/useDepositSchedule';
import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import type { ReactNode } from 'react';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { DepositAmount } from '../DepositAmount';
import { showsRequiredFieldError } from '../depositFieldErrors';
import { DepositEftBankAccount } from './DepositEftBankAccount';
import { DepositEftScheduledDate } from './DepositEftScheduledDate';
import { DepositEftScheduleSelect } from './DepositEftScheduleSelect';
import { DepositEftStarting } from './DepositEftStarting';
import type { useDepositEft } from './useDepositEft';

type EftFieldOption = {
  enabled?: boolean;
  required?: FieldRequiredType;
  label?: unknown;
};

export type DepositEftProp = {
  deposit: TransferContextData;
  options: DepositScheduleOptions & {
    subAccount?: EftFieldOption;
    bankAccount?: EftFieldOption;
    holdings?: EftFieldOption;
    fiscalYear?: EftFieldOption;
    amountCents?: EftFieldOption;
    schedule?: EftFieldOption | null;
    scheduledDate?: EftFieldOption | null;
    manualbankAccountLinking?: boolean;
    automatedBankAccountLinking?: boolean;
    displayCustodianAccountNumber?: boolean;
    selectFirstSubAccountOnly?: boolean;
  };
  focused: string[];
  onFocus: (field: string) => void;
  onChange: (value: Partial<TransferContextData>) => void;
  amountError: { hasError: boolean; errorMessage: string };
  userId?: string;
  transfer: ReturnType<typeof useDepositEft>;
  schedule: ReturnType<typeof useDepositSchedule>;
  scheduleError: string;
  totalTransfers: number | null;
  refreshTotalTransfers: (subAccount: { id: string }) => Promise<void>;
  onFormEnabledChange: (enabled: boolean) => void;
  fiscalYearMenuItems: ReactNode[];
  isSubmitted: boolean;
};

export const DepositEft = ({
  deposit,
  options,
  focused,
  onFocus,
  onChange,
  amountError,
  userId,
  transfer,
  schedule,
  scheduleError,
  totalTransfers,
  refreshTotalTransfers,
  onFormEnabledChange,
  fiscalYearMenuItems,
  isSubmitted,
}: DepositEftProp) => {
  const { translateBackendString } = useLocalization();
  const {
    enableMultiCurrencyDepositTransfers,
    subAccountFilter,
    bankAccountsQueryUserId,
    isInitiatedByRepresentative,
    amountCurrency,
    includeCashHoldings,
    isFormEnabledFor,
    dateHasChanged,
    changeScheduledDate,
  } = transfer;

  const isOneTime = deposit?.schedule === Frequency.OneTime;
  const isScheduledDateTouched =
    dateHasChanged || focused.includes('scheduledDate');

  const scheduledDateField = (
    <DepositEftScheduledDate
      scheduledDate={deposit?.scheduledDate}
      options={options}
      schedule={schedule}
      isTouched={isScheduledDateTouched}
      onDateChange={changeScheduledDate}
    />
  );

  return (
    <>
      {options?.subAccount?.enabled &&
        deposit?.objectType !== PageObjectType.SUB_ACCOUNT && (
          <SubAccountSelect
            omitAny
            sx={{ mb: 2 }}
            filter={subAccountFilter}
            dataTestId='deposit-form-subaccount-select'
            label={translateBackendString(options?.subAccount?.label)}
            onSubAccountSelect={async (e: any) => {
              onFormEnabledChange(isFormEnabledFor(e));
              onChange({ subAccount: e, bankAccount: undefined });
              await refreshTotalTransfers(e);
            }}
            selectedSubAccount={deposit?.subAccount}
            onBlur={() => onFocus('subAccount')}
            error={showsRequiredFieldError({
              missing: !deposit?.subAccount?.id,
              field: 'subAccount',
              focused,
              requirement: options?.subAccount?.required,
            })}
            showCurrencyAvatar={enableMultiCurrencyDepositTransfers}
            displayCustodianAccountNumber={
              options.displayCustodianAccountNumber
            }
            selectFirstSubAccountOnly={options.selectFirstSubAccountOnly}
          />
        )}
      {options?.bankAccount?.enabled && (
        <DepositEftBankAccount
          deposit={deposit}
          options={options}
          queryUserId={bankAccountsQueryUserId}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
          showSelectedIcon={enableMultiCurrencyDepositTransfers}
        />
      )}
      {options?.holdings?.enabled && (
        <HoldingSelectField
          userId={userId ?? ''}
          sx={{ mb: 2 }}
          disabled={!deposit?.subAccount?.account?.id}
          label={translateBackendString(options?.holdings?.label)}
          setAccountHolding={(e: any) => onChange({ holdings: e })}
          onBlur={() => onFocus('holdings')}
          selectedSubAccount={deposit?.subAccount}
          selectedHolding={deposit?.holdings}
          error={
            !deposit?.holdings?.financialProduct?.cusip &&
            options?.holdings?.required !== FieldRequiredType.NOT_REQUIRED
          }
          isSubmitted={isSubmitted}
          options={options}
          holdingFilterOptions={{ includeCash: includeCashHoldings }}
        />
      )}
      {options?.fiscalYear?.enabled &&
        showFiscalYearBasedOnAccountType(
          deposit?.subAccount?.account?.type as string,
        ) && (
          <SelectField
            fullWidth
            sx={{ mb: 2 }}
            testId={FISCAL_YEAR_SELECT_TEST_ID.SELECT}
            value={deposit?.fiscalYear}
            label={translateBackendString(options?.fiscalYear?.label)}
            onChange={(e: any) => onChange({ fiscalYear: e.target.value })}
          >
            {fiscalYearMenuItems}
          </SelectField>
        )}
      {options?.amountCents?.enabled && (
        <DepositAmount
          amountCents={deposit?.amountCents}
          label={translateBackendString(options.amountCents.label)}
          requirement={options.amountCents.required}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
          amountError={amountError}
          disabled={!deposit?.subAccount || totalTransfers === null}
          currency={amountCurrency}
          sx={{ mb: 2 }}
        />
      )}
      {options?.schedule?.enabled && (
        <DepositEftScheduleSelect
          schedule={deposit?.schedule}
          defaultScheduleOption={schedule.defaultScheduleOption}
          option={options.schedule}
          focused={focused}
          onFocus={onFocus}
          onChange={onChange}
          scheduleError={scheduleError}
        />
      )}
      {options?.scheduledDate?.enabled && !isOneTime && scheduledDateField}
      {(options?.allowOneTimeTransfersFutureDate ||
        options?.allowOneTimeTransfersNow) &&
        isOneTime && (
          <DepositEftStarting
            starting={schedule.starting}
            isInitiatedByRepresentative={isInitiatedByRepresentative}
            allowNow={options?.allowOneTimeTransfersNow}
            allowFutureDate={options?.allowOneTimeTransfersFutureDate}
            onStartingChange={schedule.setStarting}
            onFocus={onFocus}
            onChange={onChange}
          />
        )}
      {schedule.starting === DepositStarting.FutureDate &&
        isOneTime &&
        scheduledDateField}
    </>
  );
};
